import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Last line of defence against a blank page.
 *
 * The decrypt path already isolates failures per blob, but everything *after*
 * the merge — building the view model, pricing, rendering — runs synchronously
 * inside React. An exception there has nothing to catch it, and React unmounts
 * the whole tree, which is precisely the total-blindness outcome the rest of
 * this page is built to avoid. A white screen also gives the reader no way to
 * reach Settings and correct whatever caused it.
 *
 * This shows the error and a way out instead. It deliberately does *not* offer
 * to clear settings: the passphrase is unrecoverable, and a crash is not a
 * reason to destroy it.
 */
interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the only place with room for a component stack, and this
    // page has no server to report to.
    console.error("dashboard crashed", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="mx-auto w-full max-w-lg flex-1 px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-semibold tracking-tight">StatsAI</h1>
        <div className="mt-5 rounded-xl border border-critical/30 bg-critical/10 px-4 py-3">
          <p className="text-sm text-critical">The dashboard failed to render.</p>
          <p className="mt-1.5 font-mono text-[11px] break-words text-muted">
            {error.message}
          </p>
        </div>
        <p className="mt-4 text-xs text-muted">
          Your settings are untouched. Reloading is usually enough; if it
          persists, the stored data may be from a newer version of this app.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded-lg bg-surface-3 px-4 py-2 text-sm font-medium transition-colors hover:bg-border-strong"
        >
          Reload
        </button>
      </main>
    );
  }
}
